import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Typography, 
  Box,
  Grid,
  Button,
  Card,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Checkbox,
  IconButton,
  useTheme,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Grow,
  Slide,
  Zoom
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { 
  generateExam, 
  checkGenerationStatus, 
  downloadExam, 
  fetchAvailableTopics,
  fetchAvailableMarks,
  fetchTrialStatus,
  fetchCourses
} from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TopicTree = ({ 
  topics, 
  selectedTopics, 
  onSelectionChange, 
  expansionState,
  onToggleExpand,
  onFoldAll,
  onUnfoldAll
}) => {
  const theme = useTheme();
  const selectedSet = new Set(selectedTopics);
  const [searchTerm, setSearchTerm] = useState('');

  const getAllNodePaths = useCallback((node, currentPath = '', paths = new Set()) => {
    if (!node) return paths;

    if (Array.isArray(node)) {
    } else if (typeof node === 'object') {
      Object.keys(node).forEach(name => {
        const childPath = currentPath ? `${currentPath}/${name}` : name;
        paths.add(childPath);
        getAllNodePaths(node[name], childPath, paths);
      });
    }
    return paths;
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const { filteredTopics, expansionUpdates } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const newExpansionUpdates = {};

    function filterNodes(node, currentPath = '') {
      if (!node) return null;

      if (Array.isArray(node)) {
        const matchingLeaves = node.filter(leaf => leaf.toLowerCase().includes(term));
        return matchingLeaves.length > 0 ? matchingLeaves : null;
      }

      if (typeof node === 'object') {
        const filteredNode = {};
        let nodeHasVisibleChildren = false;

        for (const name in node) {
          const children = node[name];
          const childPath = currentPath ? `${currentPath}/${name}` : name;
          const childNameLower = name.toLowerCase();
          const doesNameMatch = childNameLower.includes(term);

          const filteredChildren = filterNodes(children, childPath);

          if (filteredChildren !== null || doesNameMatch) {
            filteredNode[name] = filteredChildren === null ? children : filteredChildren;
            nodeHasVisibleChildren = true;
            if (filteredChildren !== null && !doesNameMatch) {
               newExpansionUpdates[childPath] = true;
            }
          }
        }

        return nodeHasVisibleChildren ? filteredNode : null;
      }
      return null;
    }

    if (!term) {
      return { filteredTopics: topics, expansionUpdates: {} };
    }

    const result = filterNodes(topics);
    return { filteredTopics: result || {}, expansionUpdates: newExpansionUpdates };

  }, [topics, searchTerm]);

  useEffect(() => {
    if (Object.keys(expansionUpdates).length > 0) {
      Object.keys(expansionUpdates).forEach(path => {
        if (!expansionState[path]) {
          onToggleExpand(path);
        }
      });
    }
  }, [expansionUpdates, expansionState, onToggleExpand]);

  const TreeNode = ({ name, children, depth = 0, path = '' }) => {
    const currentPath = path ? `${path}/${name}` : name;
    const isSelected = selectedSet.has(currentPath);
    const hasChildren = children && (typeof children === 'object') && (Array.isArray(children) ? children.length > 0 : Object.keys(children).length > 0);
    
    const isExpanded = expansionState[currentPath] || false;

    const handleNodeClick = (event) => { 
        if (event.target === event.currentTarget || event.target.nodeName === 'SPAN') {
            if (hasChildren) {
              onToggleExpand(currentPath);
            }
        } 
    };
    
    const handleCheckboxToggle = (event) => {
      event.stopPropagation(); 
      const descendantPaths = getAllDescendantPaths(children, currentPath);
      onSelectionChange(currentPath, !isSelected, descendantPaths);
    };
    
    const getAllDescendantPaths = (node, currentPath) => {
        let paths = [];
        if (!node) return paths;
        if (Array.isArray(node)) {
            node.forEach(leaf => { paths.push(`${currentPath}/${leaf}`); });
        } else if (typeof node === 'object') {
            Object.entries(node).forEach(([childName, childData]) => {
                const childPath = currentPath ? `${currentPath}/${childName}` : childName;
                paths.push(childPath);
                paths = paths.concat(getAllDescendantPaths(childData, childPath));
            });
        }
        return paths;
    };

    const descendantPaths = getAllDescendantPaths(children, currentPath);
    const selectedDescendants = descendantPaths.filter(p => selectedSet.has(p));
    const isIndeterminate = isSelected ? false : (selectedDescendants.length > 0 && selectedDescendants.length < descendantPaths.length);
    const isNodeEffectivelySelected = isSelected || (descendantPaths.length > 0 && selectedDescendants.length === descendantPaths.length);

    const bgColor = depth === 0
      ? alpha(theme.palette.primary.main, 0.08)
      : depth === 1
        ? alpha(theme.palette.secondary.main, 0.05)
        : 'transparent';

    return (
      <>
        <ListItem
          component="div"
          onClick={handleNodeClick}
          sx={{
            pl: 2 + depth * 2,
            py: 0.2,
            cursor: hasChildren ? 'pointer' : 'default',
            borderLeft: depth === 0
              ? `4px solid ${theme.palette.primary.main}`
              : depth === 1
                ? `4px solid ${theme.palette.secondary.main}`
                : 'none',
            backgroundColor: bgColor,
            '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
            borderRadius: '4px',
            mb: 0.5,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}> 
             <Checkbox
                edge="start"
                checked={isNodeEffectivelySelected}
                indeterminate={isIndeterminate}
                onChange={handleCheckboxToggle}
                onClick={(e) => e.stopPropagation()} 
                size="small"
                tabIndex={-1}
              />
          </ListItemIcon>
           <ListItemIcon sx={{ minWidth: 36, ml: -2 }}>
            {hasChildren ? (
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); onToggleExpand(currentPath); }}
                aria-label={isExpanded ? "collapse" : "expand"}
                sx={{ p: 0.5 }}
              >
                {isExpanded
                  ? <ExpandLess /> 
                  : <ExpandMoreIcon />}
              </IconButton>
            ) : (
               <Box sx={{ width: 24 }} />
            )}
          </ListItemIcon>
          <ListItemText
             primary={
               <Typography
                 variant={depth === 0 ? 'body1' : 'body2'}
                 fontWeight={depth === 0 ? 500 : 400}
                 sx={{ ml: -1 }}
               >
                 {name}
               </Typography>
             }
           />
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {Array.isArray(children)
                ? children.map((item, idx) => {
                    const leafPath = `${currentPath}/${item}`;
                    const isLeafSelected = selectedSet.has(leafPath);
                    return (
                      <ListItem
                        key={idx}
                        sx={{ pl: 4 + depth * 2, py: 0, '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) } }}
                         component="div"
                      >
                         <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={isLeafSelected}
                            onChange={() => onSelectionChange(leafPath, !isLeafSelected, [])} 
                            size="small"
                            tabIndex={-1}
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography variant="body2">{item}</Typography>} 
                          sx={{ ml: 1.5 }}
                        />
                      </ListItem>
                    );
                  })
                : Object.entries(children).map(([childName, childData]) => (
                  <TreeNode key={childName} name={childName} children={childData} depth={depth + 1} path={currentPath} />
                ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <Paper elevation={2} sx={{ 
        p: 2, 
        borderRadius: 2, 
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              Step 2: Select Topics
            </Typography>
            <Typography variant="h6">
              Topics
            </Typography>
          </Box>
          {selectedTopics.length > 0 && (
            <Fade in={true} timeout={300}>
              <Chip 
                label={`${selectedTopics.length} selected`}
                color="primary"
                size="small"
              />
            </Fade>
          )}
        </Box>
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
           <TextField
               fullWidth
               size="small"
               variant="outlined"
               placeholder="Search Topics..."
               value={searchTerm}
               onChange={handleSearchChange}
               InputProps={{
                   startAdornment: (
                   <InputAdornment position="start">
                       <SearchIcon fontSize="small" />
                   </InputAdornment>
                   ),
               }}
           />
           <Tooltip title="Unfold All">
             <IconButton size="small" onClick={onUnfoldAll} aria-label="Unfold all topics">
               <UnfoldMoreIcon />
             </IconButton>
           </Tooltip>
           <Tooltip title="Fold All">
             <IconButton size="small" onClick={onFoldAll} aria-label="Fold all topics">
                <UnfoldLessIcon />
             </IconButton>
           </Tooltip>
       </Box>

      <Box sx={{ maxHeight: '350px', overflow: 'auto' }}>
        <List sx={{ width: '100%' }}>
          {Object.entries(filteredTopics || {}).map(([topicName, topicData]) => (
            <TreeNode key={topicName} name={topicName} children={topicData} depth={0} path="" />
          ))}
          {searchTerm && Object.keys(filteredTopics || {}).length === 0 && (
             <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
               No topics match your search.
             </Typography>
          )}
        </List>
      </Box>
      </Paper>
  );
};

const GenerateExam = () => {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState('all');
  const [mcqMarks, setMcqMarks] = useState(10);
  const [extendedMarks, setExtendedMarks] = useState(20);
  const [showControls, setShowControls] = useState(false);
  const [maxMcqMarks, setMaxMcqMarks] = useState(50);
  const [maxExtendedMarks, setMaxExtendedMarks] = useState(100);
  const [availableTopics, setAvailableTopics] = useState({});
  const [expansionState, setExpansionState] = useState({});

  const [generating, setGenerating] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  const intervalRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const controlsTimeoutRef = useRef(null);

  const handleToggleExpand = useCallback((path) => {
    setExpansionState(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const getAllNodePaths = useCallback((node, currentPath = '', paths = new Set()) => {
    if (!node) return paths;
    if (Array.isArray(node)) { /* no paths for leaves */ }
    else if (typeof node === 'object') {
      Object.keys(node).forEach(name => {
        const childPath = currentPath ? `${currentPath}/${name}` : name;
        paths.add(childPath);
        getAllNodePaths(node[name], childPath, paths);
      });
    }
    return paths;
  }, []);

  const handleFoldAll = useCallback(() => {
    setExpansionState({});
  }, []);

  const handleUnfoldAll = useCallback(() => {
    const allPaths = getAllNodePaths(availableTopics);
    const nextExpansionState = {};
    allPaths.forEach(path => { nextExpansionState[path] = true; });
    setExpansionState(nextExpansionState);
  }, [availableTopics, getAllNodePaths]);

  const handleTopicSelectionChange = useCallback((toggledPath, isSelected, descendantPaths) => {
    setSelectedTopics(prevSelected => {
      const newSelected = new Set(prevSelected);
      
      if (isSelected) {
        newSelected.add(toggledPath);
        descendantPaths.forEach(path => newSelected.add(path));

        const parts = toggledPath.split('/');
        for (let i = parts.length - 1; i > 0; i--) {
        }

      } else {
        newSelected.delete(toggledPath);
        descendantPaths.forEach(path => newSelected.delete(path));
        
        const parts = toggledPath.split('/');
        for (let i = parts.length - 1; i > 0; i--) {
            const ancestorPath = parts.slice(0, i).join('/');
            newSelected.delete(ancestorPath);
        }
      }
      return Array.from(newSelected);
    });
  }, []);

  // Show controls with animation when topics are selected
  useEffect(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (selectedTopics.length > 0 && !showControls) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(true);
      }, 300);
    } else if (selectedTopics.length === 0 && showControls) {
      setShowControls(false);
    }
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [selectedTopics, showControls]);

  // Fetch courses on component mount
  useEffect(() => {
    // Fetch trial status for students
    if (user?.role === 'STUDENT') {
      fetchTrialStatus()
        .then(res => setTrialStatus(res.data))
        .catch(err => console.error('Failed to fetch trial status:', err));
    }

    fetchCourses()
      .then(res => {
        setCourses(res.data);
        // Select first course by default if available
        if (res.data.length > 0 && res.data[0]?.id) {
          setSelectedCourse(res.data[0].id);
        } else {
          setAvailableTopics({});
          setSelectedTopics([]);
          setExpansionState({});
        }
      })
    .catch(err => {
        console.error('Failed to fetch courses:', err);
        setError('Failed to load courses. Please try refreshing.');
      });
  }, [user]);

  // Fetch topics when course is selected
  useEffect(() => {
    if (selectedCourse) {
      fetchAvailableTopics(selectedCourse)
        .then(res => {
          setAvailableTopics(res.data);
          setExpansionState({});
          // Clear selected topics when course changes
          setSelectedTopics([]);
        })
        .catch(err => {
          console.error('Failed to fetch available topics:', err);
          setError('Failed to load topics. Please try refreshing.');
          setAvailableTopics({}); 
          setExpansionState({});
        });
    }
  }, [selectedCourse]);

  useEffect(() => {
    const updateAvailableMarks = async () => {
      // Don't fetch marks if no topics are selected
      if (selectedTopics.length === 0) {
        setMaxMcqMarks(0);
        setMaxExtendedMarks(0);
        setMcqMarks(0);
        setExtendedMarks(0);
        return;
      }

      try {
        const response = await fetchAvailableMarks(selectedTopics, difficulty, selectedCourse);
        const { mcq, extended } = response.data;
        setMaxMcqMarks(mcq);
        setMaxExtendedMarks(extended);

        // Set default values to reasonable defaults when first showing controls
        if (!showControls && selectedTopics.length > 0) {
          setMcqMarks(Math.min(10, mcq));
          setExtendedMarks(Math.min(20, extended));
        } else {
          setMcqMarks(current => Math.min(current, mcq));
          setExtendedMarks(current => Math.min(current, extended));
        }

      } catch (err) {
        console.error('Failed to fetch available marks:', err);
        setMaxMcqMarks(0);
        setMaxExtendedMarks(0);
        setError('Failed to update available marks based on selection.');
      }
    };

    updateAvailableMarks();

  }, [selectedTopics, difficulty, selectedCourse]);

  const handleDifficultyChange = (event, newDifficulty) => {
    if (newDifficulty !== null) {
      setDifficulty(newDifficulty);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedTopics.length === 0) {
      setError("Please select at least one topic.");
      return;
    }
    if (mcqMarks === 0 && extendedMarks === 0) {
       setError("Please allocate marks for MCQ or Extended Response questions.");
       return;
     }

    try {
      setGenerating(true);
      setError(null);
      setTaskStatus(null);
      setTaskId(null);
      setDownloadTriggered(false); // Reset download flag for new generation
      downloadTriggeredRef.current = false; // Reset ref as well

      const params = {
        topics_include: selectedTopics,
        difficulty_range: difficulty,
        mcq_marks: mcqMarks,
        extended_marks: extendedMarks,
      };
      
      const response = await generateExam(params);
      setTaskId(response.data.task_id);
      setTaskStatus({
         task_id: response.data.task_id,
         status: 'queued',
         progress: 0.0,
         message: 'Task queued'
       });

      // Store task ID in ref to avoid closure issues
      const currentTaskId = response.data.task_id;
      
      // Initial status check
      checkStatus(currentTaskId);
      
      // Create polling function that uses the ref
      const pollStatus = () => {
        if (intervalRef.current) {
          checkStatus(currentTaskId);
        }
      };
      
      const interval = setInterval(pollStatus, 500); // Poll every 0.5 seconds
      
      intervalRef.current = interval;
      
      // Test that setInterval is working
      setTimeout(() => {
        console.log('Test: 1 second passed, interval should still be running:', intervalRef.current);
      }, 1000);
      
    } catch (err) {
      console.error('Failed to start exam generation:', err);
      const errorDetail = err.response?.data?.detail || 'Please check API connectivity.';
      setError(`Failed to start exam generation: ${errorDetail}`);
      setGenerating(false);
       if (intervalRef.current) {
         clearInterval(intervalRef.current);
         intervalRef.current = null;
       }
    }
  };
  
  const checkStatus = async (id) => {
    if (!id) return;
    
    try {
      const response = await checkGenerationStatus(id);
       if (taskStatus?.status === 'queued' && response.data.status === 'queued') {
         return; 
       }
      setTaskStatus(response.data);
      
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        // Clear interval immediately using ref
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setGenerating(false);
         if (response.data.status === 'failed') {
           setError(`Exam generation failed: ${response.data.message || 'Unknown error'}`);
           setDownloadTriggered(false); // Reset on failure
           downloadTriggeredRef.current = false;
         } else if (response.data.status === 'completed' && !downloadTriggeredRef.current) {
           // Auto-download the exam when generation is complete (only once)
           downloadTriggeredRef.current = true;
           setDownloadTriggered(true);
           handleDownload(id);
           
           // Refresh trial status for students
           if (user?.role === 'STUDENT') {
             fetchTrialStatus()
               .then(res => setTrialStatus(res.data))
               .catch(err => console.error('Failed to refresh trial status:', err));
           }
         }
      } else {
         setGenerating(true); 
       }
    } catch (err) {
      console.error('Failed to check task status:', err);
      // Clear both ref and state
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setGenerating(false);
      setError('Failed to check task status. Please check progress manually or try again.');
    }
  };
  
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount
  
  const handleDownload = async (examId) => {
    try {
      const downloadId = examId || taskId;
      if (!downloadId) {
        setError('No exam ID available for download');
        return;
      }
      
      const response = await downloadExam(downloadId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'exam';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      } else {
           const ext = response.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
           filename = `exam_${downloadId.substring(0, 8)}${ext}`;
       }
      
      if (!/\.(pdf|tex)$/i.test(filename)) {
         const ext = response.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
         filename += ext;
       }

      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Failed to download exam:', err);
      const errorDetail = err.response?.data?.detail || 'Please try again later.';
      setError(`Failed to download exam: ${errorDetail}`);
    }
  };

  const getActiveStep = () => {
    if (!taskStatus) return -1;
    
    switch (taskStatus.status) {
      case 'queued':
        return 0;
      case 'running':
         return 1; 
      case 'completed':
        return 3;
      case 'failed':
        return -1;
      default:
        return -1;
    }
  };
  
  const estimatedDuration = Math.ceil((mcqMarks + extendedMarks) / 1.2);

  // Calculate exam usage for students using API data
  const examLimit = trialStatus?.exams_limit || 0;
  const examsUsed = trialStatus?.exams_used || 0;
  const examsRemaining = trialStatus?.exams_remaining || 0;
  const hasReachedLimit = user?.role === 'STUDENT' && trialStatus && examsUsed >= examLimit;
  const isApproachingLimit = user?.role === 'STUDENT' && trialStatus && examsRemaining === 1;

  // Calculate current step based on user progress
  const getCurrentStep = () => {
    if (!selectedCourse) return 0;
    if (selectedTopics.length === 0) return 1;
    if (!showControls) return 1;
    if (mcqMarks === 0 && extendedMarks === 0) return 2;
    return 3;
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Generate New Exam
      </Typography>
      
      {/* Progress Steps */}
      <Fade in={true} timeout={300}>
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {[
              { label: 'Select Course', icon: '1' },
              { label: 'Choose Topics', icon: '2' },
              { label: 'Set Difficulty', icon: '3' },
              { label: 'Allocate Marks', icon: '4' }
            ].map((step, index) => {
              const isActive = getCurrentStep() >= index;
              const isComplete = getCurrentStep() > index;
              
              return (
                <React.Fragment key={step.label}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    opacity: isActive ? 1 : 0.5,
                    transition: 'all 0.3s ease'
                  }}>
                    <Fade in={isActive} timeout={200 + (index * 100)}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: isComplete ? 'primary.main' : isActive ? 'primary.light' : 'action.disabled',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}>
                        {isComplete ? '✓' : step.icon}
                      </Box>
                    </Fade>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        ml: 1,
                        fontWeight: isActive ? 500 : 400,
                        color: isActive ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                  {index < 3 && (
                    <Box sx={{ 
                      flex: 1, 
                      height: 2, 
                      backgroundColor: isComplete ? 'primary.main' : 'action.disabled',
                      mx: 2,
                      transition: 'all 0.3s ease'
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      </Fade>
      
      {/* Student exam usage indicator */}
      {user?.role === 'STUDENT' && trialStatus && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Trial Exams Used
              </Typography>
              <Chip 
                label={`${examsUsed} / ${examLimit}`} 
                color={hasReachedLimit ? 'error' : 'primary'}
                size="small"
              />
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(examsUsed / examLimit) * 100} 
              color={hasReachedLimit ? 'error' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {hasReachedLimit 
                ? 'You have reached your trial exam limit. Please upgrade to continue.'
                : `${examsRemaining} exam${examsRemaining !== 1 ? 's' : ''} remaining in your trial`
              }
            </Typography>
          </Box>
        </Card>
      )}
      
      {/* Student exam limit warnings */}
      {user?.role === 'STUDENT' && trialStatus && (
        <>
          {hasReachedLimit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              You have reached your trial exam limit ({examLimit} exams). Please upgrade your account to continue generating exams.
            </Alert>
          )}
          {isApproachingLimit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have {examsRemaining} exam remaining in your trial. This will be your last exam before you need to upgrade.
            </Alert>
          )}
          {!hasReachedLimit && !isApproachingLimit && examsUsed > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have used {examsUsed} of {examLimit} trial exams. {examsRemaining} remaining.
            </Alert>
          )}
        </>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            
            <Grid item xs={12} md={5}>
              <Fade in={true} timeout={300}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 500 }}>
                    Step 1: Select Course
                  </Typography>
                  <FormControl fullWidth required>
                    <InputLabel id="course-select-label">Course</InputLabel>
                    <Select
                      labelId="course-select-label"
                      id="course-select"
                      value={selectedCourse || ''}
                      label="Course"
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2,
                          borderColor: 'primary.light',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      {courses.map((course) => (
                        <MenuItem key={course.id} value={course.id}>
                          {course.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Fade>
              
              {selectedCourse && (
                <Fade in={true} timeout={400}>
                  <Box>
                    <TopicTree 
                      topics={availableTopics} 
                      selectedTopics={selectedTopics} 
                      onSelectionChange={handleTopicSelectionChange} 
                      expansionState={expansionState}
                      onToggleExpand={handleToggleExpand}
                      onFoldAll={handleFoldAll}
                      onUnfoldAll={handleUnfoldAll}
                    />
                  </Box>
                </Fade>
              )}
              
              {!selectedCourse && courses.length > 0 && (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  color: 'text.secondary'
                }}>
                  <Typography>
                    Please select a course to view available topics
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={7}>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                 
                 {!showControls && selectedTopics.length === 0 && (
                   <Fade in={true} timeout={500}>
                     <Box sx={{ 
                       p: 4, 
                       textAlign: 'center', 
                       border: '2px dashed',
                       borderColor: 'primary.light',
                       borderRadius: 2,
                       backgroundColor: 'action.hover',
                       minHeight: 300,
                       display: 'flex',
                       flexDirection: 'column',
                       justifyContent: 'center',
                       alignItems: 'center'
                     }}>
                       <Typography variant="h6" color="text.secondary" gutterBottom>
                         Select topics to continue
                       </Typography>
                       <Typography variant="body2" color="text.secondary">
                         Choose at least one topic from the list to set difficulty and marks
                       </Typography>
                     </Box>
                   </Fade>
                 )}

                 <Fade in={showControls} timeout={400}>
                   <Box sx={{ display: showControls ? 'block' : 'none' }}>
                     <Slide direction="right" in={showControls} timeout={300}>
                       <Box>
                         <Typography variant="body2" color="primary" sx={{ mb: 0.5, fontWeight: 500 }}>
                           Step 3: Choose Difficulty
                         </Typography>
                         <Typography gutterBottom variant="h6">Difficulty</Typography>
                   <ToggleButtonGroup
                     value={difficulty}
                     exclusive
                     onChange={(event, newDifficulty) => {
                       if (newDifficulty !== null) {
                         setDifficulty(newDifficulty);
                       }
                     }}
                     aria-label="exam difficulty"
                     disabled={generating}
                     fullWidth
                   >
                     <ToggleButton value="all" aria-label="mixed difficulty">
                       Mixed / All
                     </ToggleButton>
                     <ToggleButton value="easy" aria-label="easy difficulty">
                       Easy
                     </ToggleButton>
                     <ToggleButton value="medium" aria-label="medium difficulty">
                       Medium
                     </ToggleButton>
                     <ToggleButton value="hard" aria-label="hard difficulty">
                       Hard
                     </ToggleButton>
                   </ToggleButtonGroup>
                       </Box>
                     </Slide>
                   </Box>
                 </Fade>

                 <Fade in={showControls} timeout={500} style={{ transitionDelay: showControls ? '100ms' : '0ms' }}>
                   <Box sx={{ display: showControls ? 'block' : 'none' }}>
                     <Zoom in={showControls} timeout={350} style={{ transitionDelay: showControls ? '100ms' : '0ms' }}>
                       <Box>
                         <Typography variant="body2" color="primary" sx={{ mb: 0.5, fontWeight: 500 }}>
                           Step 4: Set Marks
                         </Typography>
                         <Typography gutterBottom variant="h6">Marks Allocation</Typography>
                   <Box sx={{ px: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                       <Typography sx={{ flex: 1 }}>
                         Multiple Choice Marks
                       </Typography>
                       <TextField
                         value={mcqMarks}
                         onChange={(e) => {
                           const value = parseInt(e.target.value) || 0;
                           setMcqMarks(Math.min(Math.max(0, value), maxMcqMarks));
                         }}
                         type="number"
                         size="small"
                         inputProps={{ min: 0, max: maxMcqMarks }}
                         sx={{ width: 80 }}
                         disabled={generating || maxMcqMarks === 0}
                       />
                       <Typography variant="body2" color="text.secondary">
                         / {maxMcqMarks}
                       </Typography>
                     </Box>
                     <Slider
                       value={mcqMarks}
                       onChange={(e, newValue) => setMcqMarks(newValue)}
                       aria-labelledby="mcq-marks-slider"
                       valueLabelDisplay="auto"
                       step={1}
                       marks
                       min={0}
                       max={maxMcqMarks}
                       disabled={generating || maxMcqMarks === 0}
                       sx={{
                         '& .MuiSlider-thumb': {
                           transition: 'all 0.2s ease-out',
                           '&:hover': {
                             boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)'
                           }
                         }
                       }}
                     />
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, mt: 3 }}>
                       <Typography sx={{ flex: 1 }}>
                         Extended Response Marks
                       </Typography>
                       <TextField
                         value={extendedMarks}
                         onChange={(e) => {
                           const value = parseInt(e.target.value) || 0;
                           setExtendedMarks(Math.min(Math.max(0, value), maxExtendedMarks));
                         }}
                         type="number"
                         size="small"
                         inputProps={{ min: 0, max: maxExtendedMarks }}
                         sx={{ width: 80 }}
                         disabled={generating || maxExtendedMarks === 0}
                       />
                       <Typography variant="body2" color="text.secondary">
                         / {maxExtendedMarks}
                       </Typography>
                     </Box>
                     <Slider
                       value={extendedMarks}
                       onChange={(e, newValue) => setExtendedMarks(newValue)}
                       aria-labelledby="extended-marks-slider"
                       valueLabelDisplay="auto"
                       step={1}
                       marks
                       min={0}
                       max={maxExtendedMarks}
                       disabled={generating || maxExtendedMarks === 0}
                       sx={{
                         '& .MuiSlider-thumb': {
                           transition: 'all 0.2s ease-out',
                           '&:hover': {
                             boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)'
                           }
                         }
                       }}
                     />
                   </Box>
                       </Box>
                     </Zoom>
                   </Box>
                 </Fade>

                 <Fade in={showControls} timeout={600} style={{ transitionDelay: showControls ? '200ms' : '0ms' }}>
                   <Box sx={{ display: showControls ? 'block' : 'none' }}>
                     <Grow in={showControls} timeout={400} style={{ transitionDelay: showControls ? '200ms' : '0ms' }}>
                       <Box>
                         <Typography variant="h6">Exam Information</Typography>
                    <Typography variant="body1">
                     Total Marks: {mcqMarks + extendedMarks}
                   </Typography>
                   <Typography variant="body1">
                     Estimated Duration: {estimatedDuration} minutes 
                     <Typography variant="caption" sx={{ml: 1}}>(approx. 1.2 marks/min)</Typography>
                   </Typography>
                       </Box>
                     </Grow>
                   </Box>
                 </Fade>
                 
                 <Box sx={{ mt: 2 }}>
                   <Button
                     type="submit"
                     variant="contained"
                     color="primary"
                     size="large"
                     disabled={generating || (mcqMarks === 0 && extendedMarks === 0) || selectedTopics.length === 0 || hasReachedLimit}
                     fullWidth
                   >
                     {generating ? 'Generating Exam...' : hasReachedLimit ? 'Trial Limit Reached' : 'Generate Exam'}
                   </Button>
                 </Box>

               </Box>
             </Grid>
             
          </Grid>
        </form>
      </Paper>
      
      {/* Generation Status - Below the form */}
      {taskId && (
        <Fade in={true} timeout={500}>
          <Paper sx={{ p: 4, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            {(taskStatus?.status === 'running' || taskStatus?.status === 'queued') && (
              <Box>
                {/* Animated background effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '200%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.1), transparent)',
                    animation: 'shimmer 2s infinite',
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(50%)' }
                    }
                  }}
                />
                
                {/* Main content */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {/* Multiple animated elements */}
                  <Box sx={{ mb: 3, position: 'relative', height: 120 }}>
                    {/* Central spinning circle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        border: '3px solid',
                        borderColor: 'primary.light',
                        borderTopColor: 'primary.main',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                          '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                        }
                      }}
                    />
                    
                    {/* Orbiting dots */}
                    {[0, 1, 2].map((index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 100,
                          height: 100,
                          animation: `orbit 3s linear infinite`,
                          animationDelay: `${index * 1}s`,
                          '@keyframes orbit': {
                            '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                            '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            opacity: 0.6,
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${index * 0.5}s`,
                            '@keyframes pulse': {
                              '0%, 100%': { transform: 'translateX(-50%) scale(1)', opacity: 0.6 },
                              '50%': { transform: 'translateX(-50%) scale(1.5)', opacity: 1 }
                            }
                          }}
                        />
                      </Box>
                    ))}
                    
                    {/* Pulsing ring */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        opacity: 0,
                        animation: 'ripple 2s ease-out infinite',
                        '@keyframes ripple': {
                          '0%': {
                            transform: 'translate(-50%, -50%) scale(1)',
                            opacity: 0.8
                          },
                          '100%': {
                            transform: 'translate(-50%, -50%) scale(2)',
                            opacity: 0
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Typography variant="h6" color="primary" gutterBottom>
                    Generating Your Exam
                  </Typography>
                  
                  {/* Animated text with cycling messages */}
                  <Box sx={{ height: 24, mb: 2 }}>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{
                        animation: 'fadeInOut 4s infinite',
                        '@keyframes fadeInOut': {
                          '0%, 100%': { opacity: 0 },
                          '20%, 80%': { opacity: 1 }
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          animation: 'textCycle 12s infinite',
                          '@keyframes textCycle': {
                            '0%, 33%': { content: '"Analyzing selected topics..."' },
                            '33%, 66%': { content: '"Creating question variations..."' },
                            '66%, 100%': { content: '"Finalizing your exam..."' }
                          },
                          '&::before': {
                            content: '"Analyzing selected topics..."',
                            animation: 'textContent 12s infinite step-end',
                            '@keyframes textContent': {
                              '0%': { content: '"Analyzing selected topics..."' },
                              '33%': { content: '"Creating question variations..."' },
                              '66%': { content: '"Finalizing your exam..."' }
                            }
                          }
                        }}
                      />
                    </Typography>
                  </Box>
                  
                  {/* Fancy animated progress indicator */}
                  <Box sx={{ mt: 3, mb: 2 }}>
                    {/* Wave animation */}
                    <Box
                      sx={{
                        height: 40,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5
                      }}
                    >
                      {[...Array(12)].map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: 4,
                            height: 20,
                            backgroundColor: 'primary.main',
                            borderRadius: 2,
                            animation: 'wave 1.2s ease-in-out infinite',
                            animationDelay: `${index * 0.1}s`,
                            '@keyframes wave': {
                              '0%, 100%': {
                                height: 20,
                                opacity: 0.3
                              },
                              '50%': {
                                height: 40,
                                opacity: 1
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                    
                    {/* Alternative: DNA helix animation */}
                    <Box
                      sx={{
                        mt: 2,
                        height: 6,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3,
                        backgroundColor: 'action.hover'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '100%',
                          background: `repeating-linear-gradient(
                            90deg,
                            #1976d2 0px,
                            #1976d2 20px,
                            #42a5f5 20px,
                            #42a5f5 40px
                          )`,
                          animation: 'slide 1s linear infinite',
                          '@keyframes slide': {
                            '0%': { transform: 'translateX(0)' },
                            '100%': { transform: 'translateX(40px)' }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Floating elements animation */}
                  <Box sx={{ position: 'relative', height: 30, overflow: 'hidden' }}>
                    {['MCQ', '?', '✓', 'Q'].map((char, index) => (
                      <Typography
                        key={index}
                        sx={{
                          position: 'absolute',
                          fontSize: 14,
                          color: 'primary.light',
                          opacity: 0.6,
                          animation: `float ${3 + index}s ease-in-out infinite`,
                          animationDelay: `${index * 0.7}s`,
                          left: `${20 + index * 20}%`,
                          '@keyframes float': {
                            '0%, 100%': {
                              transform: 'translateY(30px)',
                              opacity: 0
                            },
                            '50%': {
                              transform: 'translateY(-10px)',
                              opacity: 0.6
                            }
                          }
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                  </Box>
                  
                  {taskStatus?.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {taskStatus.message}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            {taskStatus?.status === 'completed' && (
              <Zoom in={true} timeout={500}>
                <Box>
                  {/* Success checkmark */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        margin: '0 auto',
                        borderRadius: '50%',
                        backgroundColor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'scaleIn 0.5s ease-out',
                        '@keyframes scaleIn': {
                          '0%': { transform: 'scale(0)' },
                          '50%': { transform: 'scale(1.1)' },
                          '100%': { transform: 'scale(1)' }
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: 'white' }}>✓</Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Exam Generated Successfully!
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    onClick={() => handleDownload(taskId)}
                    disabled={!taskId}
                    sx={{ mt: 2 }}
                  >
                    Download Exam
                  </Button>
                </Box>
              </Zoom>
            )}
            
            {taskStatus?.status === 'failed' && (
              <Zoom in={true} timeout={500}>
                <Box>
                  {/* Error icon */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        margin: '0 auto',
                        borderRadius: '50%',
                        backgroundColor: 'error.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'shake 0.5s ease-out',
                        '@keyframes shake': {
                          '0%, 100%': { transform: 'translateX(0)' },
                          '25%': { transform: 'translateX(-10px)' },
                          '75%': { transform: 'translateX(10px)' }
                        }
                      }}
                    >
                      <Typography variant="h4" sx={{ color: 'white' }}>✕</Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" color="error" gutterBottom>
                    Generation Failed
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {taskStatus?.message || 'An error occurred during generation.'}
                  </Typography>
                </Box>
              </Zoom>
            )}
          </Paper>
        </Fade>
      )}
    </div>
  );
};

export default GenerateExam; 
